import { type ChangeEvent, memo } from 'react'
import { type FieldValues, type UseControllerProps, useController } from 'react-hook-form'

import TextField, { type TextFieldProps } from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'

type Props<TValue, TFieldValues extends FieldValues> = TextFieldProps &
    UseControllerProps<TFieldValues> & {
        showTextCount?: boolean
        showErrorText?: boolean
        transform?: {
            output: (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => TValue
            input: (value: TValue) => string
        }
    }

function FormField<TValue = string, TFieldValues extends FieldValues = FieldValues>({
    name,
    control,
    defaultValue,
    rules,
    type,
    helperText,
    label,
    showTextCount,
    shouldUnregister,
    transform,
    onChange,
    onBlur,
    showErrorText = true,
    ...rest
}: Props<TValue, TFieldValues>) {
    const {
        fieldState: { error },
        field: { ref, ...field },
    } = useController({ name, control, defaultValue, shouldUnregister, rules })

    return (
        <>
            <TextField
                {...field}
                value={transform?.input(field.value) ?? field.value ?? ''}
                inputRef={ref}
                type={type}
                variant="outlined"
                margin="dense"
                label={label}
                error={!!error}
                helperText={showErrorText ? (error?.message ?? helperText) : ''}
                onChange={(e) => {
                    field.onChange(transform?.output(e) ?? e)
                    onChange?.(e)
                }}
                onBlur={(e) => {
                    field.onBlur()
                    onBlur?.(e)
                }}
                {...rest}
            />
            {showTextCount && (
                <Typography variant="body2" color="textSecondary">
                    {field.value?.length ?? 0}/{rest.inputProps?.maxLength ?? '-'}
                </Typography>
            )}
        </>
    )
}

export default memo(FormField) as typeof FormField
